import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Truck, ShieldCheck, Clock, IndianRupee, Package, Zap, ArrowRight,
  CheckCircle2, Send, Building2, Phone, Mail, Users, BarChart3, Loader2,
} from "lucide-react";
import Logo from "../components/Logo";
import api from "../services/api";
import { toast } from "sonner";

export default function B2BLanding() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [contactForm, setContactForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", monthly_volume: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/b2b/pricing-tiers").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.company_name || !contactForm.email || !contactForm.phone) {
      toast.error("Please fill company name, email & phone");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/b2b/contact", contactForm);
      setSubmitted(true);
      toast.success("Request submitted! We'll contact you within 24 hours.");
    } catch {
      toast.error("Failed to submit. Please try again.");
    }
    setSubmitting(false);
  };

  const tiers = pricing?.tiers || {};
  const slabs = pricing?.volume_slabs || [];

  return (
    <div className="min-h-screen bg-white" data-testid="b2b-landing">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="cursor-pointer" onClick={() => navigate("/")}>
            <Logo size="md" />
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/b2b/track")} className="text-sm text-slate-600 hover:text-[#FF6B00]" data-testid="b2b-track-btn">Track Shipment</Button>
            <Button variant="outline" onClick={() => navigate("/b2b/login")} className="rounded-xl text-sm" data-testid="b2b-login-btn">B2B Login</Button>
            <Button onClick={() => navigate("/b2b/register")} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl text-sm gap-1.5" data-testid="b2b-register-btn">
              <Send className="h-3.5 w-3.5" /> B2B Register
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0B132B] text-white py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/10 to-transparent" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#FF6B00]/5 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-[#FF6B00]/20 text-[#FF6B00] px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
              <Zap className="h-4 w-4" /> B2B Express Logistics
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight">
              Reliable Tier 2/3<br />Delivery with <span className="text-[#FF6B00]">SLA Guarantee</span>
            </h1>
            <p className="text-lg text-slate-300 mt-6 max-w-xl leading-relaxed">
              Express B2B logistics for MSMEs & factories. Base + per-kg pricing. Volume discounts. 
              ₹50 credit if we miss your delivery window.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Button onClick={() => document.getElementById("pricing-section")?.scrollIntoView({ behavior: "smooth" })} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-12 px-8 text-base gap-2" data-testid="b2b-view-pricing">
                View Pricing <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={() => navigate("/register")} className="border-slate-500 text-white hover:bg-white/10 rounded-xl h-12 px-8 text-base" data-testid="b2b-signup-btn">
                Start Free Pilot
              </Button>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/10">
              <div>
                <p className="font-['Outfit'] text-2xl font-bold">1-2 Day</p>
                <p className="text-sm text-slate-400 mt-0.5">Delivery SLA</p>
              </div>
              <div>
                <p className="font-['Outfit'] text-2xl font-bold">₹50</p>
                <p className="text-sm text-slate-400 mt-0.5">SLA Penalty Credit</p>
              </div>
              <div>
                <p className="font-['Outfit'] text-2xl font-bold">18-22%</p>
                <p className="text-sm text-slate-400 mt-0.5">Cost Savings</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-12">Why PickNGo for B2B?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, title: "SLA Penalty Guarantee", desc: "₹50 credit per shipment if we miss the delivery window. We put our money where our mouth is.", color: "text-emerald-600", bg: "bg-emerald-50" },
              { icon: Truck, title: "Tier 2/3 Reliability", desc: "Dedicated coverage in cities where other players struggle. Own fleet for first & last mile.", color: "text-blue-600", bg: "bg-blue-50" },
              { icon: IndianRupee, title: "Volume Discounts", desc: "As low as ₹52/shipment for Tier 1. The more you ship, the more you save.", color: "text-[#FF6B00]", bg: "bg-orange-50" },
              { icon: BarChart3, title: "Shipment Analytics", desc: "Real-time tracking, delivery reports, and performance dashboards for your business.", color: "text-violet-600", bg: "bg-violet-50" },
            ].map((v) => (
              <div key={v.title} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 card-hover">
                <div className={`w-12 h-12 ${v.bg} rounded-xl flex items-center justify-center mb-4`}>
                  <v.icon className={`h-6 w-6 ${v.color}`} />
                </div>
                <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-2">{v.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing-section" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Transparent Pricing</h2>
            <p className="text-sm text-slate-500 mt-2">Base Charge + Per-kg Rate. No hidden fees. GST extra.</p>
          </div>

          {/* Tier Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="pricing-tier-cards">
            {Object.entries(tiers).map(([key, tier], i) => (
              <div key={key} className={`rounded-2xl border-2 p-6 ${i === 1 ? "border-[#FF6B00] shadow-lg shadow-orange-500/10 relative" : "border-slate-200"}`}>
                {i === 1 && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF6B00] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">Most Popular</span>}
                <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">{tier.label}</h3>
                <p className="text-xs text-slate-400 mt-1">{tier.sla_days}-Day Delivery SLA</p>
                <div className="mt-4 mb-5">
                  <span className="font-['Outfit'] text-3xl font-extrabold text-slate-900">₹{tier.base_charge}</span>
                  <span className="text-sm text-slate-500 ml-1">base</span>
                  <span className="text-slate-400 mx-2">+</span>
                  <span className="font-['Outfit'] text-3xl font-extrabold text-[#FF6B00]">₹{tier.per_kg_rate}</span>
                  <span className="text-sm text-slate-500 ml-1">/kg</span>
                </div>
                <div className="space-y-2.5 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>{tier.sla_days === 1 ? "Next-day delivery" : "2-day delivery"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>₹50 SLA penalty guarantee</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>Real-time GPS tracking</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                    <span>OTP-verified delivery</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Volume Slab Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="volume-slab-table">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Volume Discounts</h3>
              <p className="text-xs text-slate-400 mt-0.5">Price per 3kg shipment. Higher volumes = lower rates.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Monthly Volume</th>
                    <th className="text-center py-3 px-5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Tier 1</th>
                    <th className="text-center py-3 px-5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Tier 2</th>
                    <th className="text-center py-3 px-5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Tier 3</th>
                  </tr>
                </thead>
                <tbody>
                  {slabs.map((slab, i) => (
                    <tr key={slab.label} className={`border-t border-slate-100 ${i === 1 ? "bg-orange-50/40" : ""}`}>
                      <td className="py-3.5 px-5 font-semibold text-slate-700">{slab.label}</td>
                      <td className="py-3.5 px-5 text-center font-bold text-slate-900">₹{slab.prices.tier_1}</td>
                      <td className="py-3.5 px-5 text-center font-bold text-slate-900">₹{slab.prices.tier_2}</td>
                      <td className="py-3.5 px-5 text-center font-bold text-slate-900">₹{slab.prices.tier_3}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add-ons */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="addon-cards">
            {[
              { label: "Express Packing", price: "₹25-65/shipment", desc: "Professional packing. Mandatory for fragile.", icon: Package },
              { label: "COD Collection", price: "1.5% of invoice", desc: "Cash on delivery with next-day remittance.", icon: IndianRupee },
              { label: "Insurance Cover", price: "0.8% of value", desc: "Full coverage on declared shipment value.", icon: ShieldCheck },
              { label: "Weekend Pickup", price: "₹30 surcharge", desc: "Saturday & Sunday pickup available.", icon: Clock },
              { label: "Reverse Logistics", price: "₹55-95/return", desc: "Return shipment handling & pickup.", icon: Truck },
              { label: "SLA Penalty", price: "₹50 credit", desc: "Automatic wallet credit if SLA is missed.", icon: Zap },
            ].map((a) => (
              <div key={a.label} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="w-9 h-9 bg-white rounded-lg border border-slate-200 flex items-center justify-center shrink-0">
                  <a.icon className="h-4 w-4 text-[#FF6B00]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{a.label}</p>
                  <p className="text-xs text-[#FF6B00] font-bold">{a.price}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-12">How B2B Express Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Book Shipment", desc: "Enter pickup & delivery city, weight, and add-ons. Get instant pricing.", color: "bg-blue-500" },
              { step: "2", title: "Agent Pickup", desc: "Our rider arrives at your location within the SLA window to collect the package.", color: "bg-[#FF6B00]" },
              { step: "3", title: "Hub Processing", desc: "Sorted at origin hub, line-hauled overnight to destination hub for Tier 2/3.", color: "bg-violet-500" },
              { step: "4", title: "OTP Delivery", desc: "Last-mile delivery with OTP verification. ₹50 credit if SLA is missed.", color: "bg-emerald-500" },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className={`w-14 h-14 ${s.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                  <span className="text-white text-xl font-bold">{s.step}</span>
                </div>
                <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-1">{s.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-section" className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900">Get Started with B2B Express</h2>
            <p className="text-sm text-slate-500 mt-2">Request a demo or start a free 30-day pilot</p>
          </div>

          {submitted ? (
            <div className="text-center py-12 bg-emerald-50 rounded-2xl border border-emerald-200" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
              <h3 className="font-['Outfit'] text-xl font-bold text-slate-900">Thank You!</h3>
              <p className="text-sm text-slate-600 mt-2">Our team will contact you within 24 hours to set up your pilot.</p>
            </div>
          ) : (
            <form onSubmit={handleContact} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8 space-y-5" data-testid="b2b-contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Company Name *</Label>
                  <Input placeholder="Your company" value={contactForm.company_name} onChange={(e) => setContactForm({ ...contactForm, company_name: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="contact-company" required />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Contact Person *</Label>
                  <Input placeholder="Full name" value={contactForm.contact_name} onChange={(e) => setContactForm({ ...contactForm, contact_name: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="contact-name" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Email *</Label>
                  <Input type="email" placeholder="you@company.com" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="contact-email" required />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Phone *</Label>
                  <Input placeholder="+91 9876543210" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="contact-phone" required />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Expected Monthly Shipments</Label>
                <Input placeholder="e.g., 200-500" value={contactForm.monthly_volume} onChange={(e) => setContactForm({ ...contactForm, monthly_volume: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="contact-volume" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Message</Label>
                <textarea placeholder="Tell us about your logistics needs..." value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} className="w-full rounded-xl mt-1 border border-slate-200 px-4 py-3 text-sm resize-none h-24 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20 focus:outline-none focus:ring-2" data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-12 text-base font-semibold gap-2" data-testid="contact-submit">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                {submitting ? "Submitting..." : "Request Demo"}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0B132B] text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Logo size="md" textColor="text-white" />
          <p className="text-sm text-slate-400 mt-3">B2B Express Logistics — Tier 2/3 cities, delivered reliably.</p>
          <p className="text-xs text-slate-500 mt-4">&copy; {new Date().getFullYear()} PickNGo. All rights reserved.</p>
          <div className="flex items-center justify-center gap-5 mt-3">
            <a href="/privacy" className="text-xs text-slate-400 hover:text-[#FF6B00] transition-colors no-underline" data-testid="b2b-footer-privacy-link">Privacy Policy</a>
            <a href="/terms" className="text-xs text-slate-400 hover:text-[#FF6B00] transition-colors no-underline" data-testid="b2b-footer-terms-link">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
